/**
 * UkFeColleges — the `/uk-fe-colleges` landing page.
 *
 * Turns the 284 UK FE colleges + specialist HE providers we cover
 * from a hidden liability into a differentiating landing page.
 * Nobody (UCAS, THE, QS, TSR) has a proper editorial index of this
 * segment, and yet 1.6M UK students study in it. Positioning: the
 * honest "which of these is the right stepping stone / vocational
 * route for you" guide, not a rebadged Russell Group table.
 *
 * Data: `GET /api/universities/uk-fe-colleges` returns names, cities,
 * institution_type slugs and first-sentence snippets (or an empty
 * snippet if the LLM writeup hasn't landed yet).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Filter, School, Sparkles, Loader2, Check } from "lucide-react";
import OfstedChip from "@/components/app/OfstedChip";

const TYPE_LABELS = {
  further_education_college: "FE college",
  further_education_college_group: "FE college group",
  university_centre: "University centre",
  specialist_conservatoire: "Conservatoire",
  specialist_he_provider: "Specialist HE",
  specialist_he_college: "Specialist HE",
  private_he_provider: "Private HE",
  conservatoire: "Conservatoire",
};

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "further_education_college", label: "FE colleges" },
  { value: "university_centre", label: "University centres" },
  { value: "specialist_he_provider", label: "Specialist HE" },
  { value: "private_he_provider", label: "Private HE" },
  { value: "specialist_conservatoire", label: "Conservatoires" },
];

export default function UkFeColleges() {
  const [state, setState] = useState({ loading: true, data: null, err: null });
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  // Multi-select for the compare flow. Max 3 to keep the compare view
  // legible on desktop; capping is enforced when the user tries to
  // tick a 4th (silently ignored — the tick just doesn't take).
  const [selected, setSelected] = useState([]);
  const toggleSelected = (id) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 3) return cur;
      return [...cur, id];
    });
  };

  useEffect(() => {
    api
      .get("/universities/uk-fe-colleges?limit=500")
      .then((r) => setState({ loading: false, data: r.data, err: null }))
      .catch((e) => setState({ loading: false, data: null, err: e }));
  }, []);

  const filtered = useMemo(() => {
    const entries = state.data?.entries || [];
    const qLower = q.trim().toLowerCase();
    return entries.filter((e) => {
      if (typeFilter !== "all" && e.institution_type !== typeFilter) return false;
      if (!qLower) return true;
      return (
        (e.name || "").toLowerCase().includes(qLower) ||
        (e.city || "").toLowerCase().includes(qLower)
      );
    });
  }, [state.data, q, typeFilter]);

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const e of state.data?.entries || []) {
      counts[e.institution_type || "unknown"] =
        (counts[e.institution_type || "unknown"] || 0) + 1;
    }
    return counts;
  }, [state.data]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="uk-fe-colleges-page">
      {/* Hero */}
      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> UK further education & specialist HE
        </div>
        <h1
          data-testid="uk-fe-colleges-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          The UK's other higher-education routes
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed text-lg">
          Nearly 300 UK institutions offer degree-level study outside the
          traditional 3-year university route — FE colleges running foundation
          degrees and HNDs, specialist arts and religious providers,
          university centres and private HE colleges.
        </p>
        <p className="mt-3 text-stone-600 max-w-3xl leading-relaxed">
          If you missed your A-level offer, want to top up an HND, need a lower
          tuition price, are changing careers as a mature learner or want a
          specialist provider (music, drama, theology, chiropractic) — this is
          where to look. We index every one, mark clearly what they are, and
          link straight to the provider so you can verify before applying.
        </p>
      </div>

      {/* Trust bar */}
      {state.data && (
        <div
          data-testid="uk-fe-colleges-trust-bar"
          className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex flex-wrap gap-4 items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <School className="h-4 w-4 text-emerald-700" />
            <div className="text-[13px] text-stone-700">
              <span className="font-mono font-semibold text-[#002147]">
                {state.data.total}
              </span>{" "}
              UK institutions indexed ·{" "}
              <span className="font-mono font-semibold text-[#002147]">
                {state.data.with_writeup}
              </span>{" "}
              with our editorial write-up so far
            </div>
          </div>
          <Link
            to="/advanced?inst=further_education_college"
            data-testid="uk-fe-narrow-match-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-3 py-1.5 text-[11px] font-underground-sc tracking-wider hover:bg-emerald-800"
          >
            Narrow my advanced match to just FE colleges →
          </Link>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <input
            data-testid="uk-fe-colleges-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or city (e.g. Blackpool, London, theology)"
            className="w-full rounded-md border border-stone-300 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4C1D95]/30"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-stone-500" />
          {TYPE_FILTERS.map((t) => {
            const count = t.value === "all"
              ? state.data?.entries?.length || 0
              : typeCounts[t.value] || 0;
            const active = typeFilter === t.value;
            return (
              <button
                key={t.value}
                data-testid={`uk-fe-colleges-filter-${t.value}`}
                onClick={() => setTypeFilter(t.value)}
                className={
                  "text-[11px] font-underground-sc tracking-wider rounded-full border px-2.5 py-1 transition-colors " +
                  (active
                    ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                    : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]/40")
                }
              >
                {t.label} <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      {state.loading && (
        <div className="mt-10 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading institutions…
        </div>
      )}

      {!state.loading && filtered.length === 0 && (
        <div
          data-testid="uk-fe-colleges-empty"
          className="mt-10 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center text-stone-500"
        >
          No institutions match that search.
        </div>
      )}

      {!state.loading && filtered.length > 0 && (
        <div
          data-testid="uk-fe-colleges-grid"
          className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filtered.map((e) => {
            const isSelected = selected.includes(e.id);
            return (
              <div
                key={e.id}
                data-testid={`uk-fe-college-card-${e.id}`}
                className={`group relative rounded-md border bg-white p-4 hover:shadow-sm transition-all ${
                  isSelected
                    ? "border-[#4C1D95] ring-1 ring-[#4C1D95]"
                    : "border-stone-200 hover:border-[#4C1D95]/40"
                }`}
              >
                {/* Compare tick — click stops propagation so it doesn't
                    fire the card link. Only one visible target per card
                    so no confusion about what tapping does. */}
                <button
                  type="button"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    toggleSelected(e.id);
                  }}
                  data-testid={`uk-fe-college-card-${e.id}-select`}
                  aria-pressed={isSelected}
                  aria-label={isSelected ? "Remove from compare" : "Add to compare"}
                  className={`absolute top-3 right-3 h-5 w-5 rounded border flex items-center justify-center transition-colors ${
                    isSelected
                      ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                      : "border-stone-300 bg-white text-transparent hover:border-[#4C1D95] hover:text-stone-400"
                  }`}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </button>

                <Link to={`/uni/${e.id}`} className="block">
                  <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 pr-8">
                    {TYPE_LABELS[e.institution_type] || "Higher education provider"}
                  </div>
                  <div className="mt-1 font-editorial text-lg text-[#002147] leading-snug group-hover:text-[#4C1D95] pr-8">
                    {e.name}
                  </div>
                  <div className="mt-0.5 text-[12px] text-stone-500">{e.city}</div>
                  {e.ofsted_rating && (
                    <div className="mt-2">
                      <OfstedChip
                        rating={e.ofsted_rating}
                        inspectionDate={e.ofsted_inspection_date}
                      />
                    </div>
                  )}
                  {e.has_writeup ? (
                    <p className="mt-2 text-[13px] text-stone-700 leading-snug line-clamp-3">
                      {e.snippet}
                    </p>
                  ) : (
                    <div
                      data-testid={`uk-fe-college-card-${e.id}-no-writeup`}
                      className="mt-2 text-[11px] font-mono text-stone-400"
                    >
                      Editorial write-up queued
                    </div>
                  )}
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky compare bar — appears when the user has ticked ≥2 providers */}
      {selected.length >= 2 && (
        <div
          data-testid="uk-fe-compare-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-full bg-[#002147] text-white px-4 py-2.5 shadow-lg flex items-center gap-3 text-[13px]"
        >
          <span className="font-underground-sc tracking-wider text-[11px] opacity-70">
            {selected.length} selected
          </span>
          <Link
            to={`/uk-fe-colleges/compare?ids=${selected.join(",")}`}
            data-testid="uk-fe-compare-cta"
            className="rounded-full bg-white text-[#002147] px-3 py-1 text-[11px] font-underground-sc tracking-wider hover:bg-stone-100"
          >
            Compare →
          </Link>
          <button
            type="button"
            data-testid="uk-fe-compare-clear"
            onClick={() => setSelected([])}
            className="text-[11px] font-underground-sc tracking-wider text-white/70 hover:text-white"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
